import { ActivityFeed, ActivityFeedUnion, ActivityType } from "@/types/v2";
import { firestore } from "@/services/firestore";
import { SearchOptions } from "@/types/v2/Firestore";
import { DocumentSnapshot } from "firebase/firestore";
import { addDays } from "date-fns";

export const ActivityFeedRepository = {
  subscribeAll: (
    shopId: string,
    callback: (doc: ActivityFeed[] | undefined) => void
  ) => {
    return firestore.subscribeCollection<ActivityFeed>(
      `shops/${shopId}/activityFeed`,
      callback,
      undefined,
      250,
      "createdAt",
      "desc"
    );
  },

  subscribeAllUnion: (
    shopId: string,
    callback: (doc: ActivityFeedUnion[] | undefined) => void,
    limitResults: number = 250
  ) => {
    return firestore.subscribeCollection<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      callback,
      undefined,
      limitResults,
      "createdAt",
      "desc"
    );
  },

  subscribeByType: (
    shopId: string,
    activityType: ActivityType,
    callback: (doc: ActivityFeedUnion[] | undefined) => void
  ) => {
    const searchOptions: SearchOptions<ActivityFeedUnion> = {
      searchParam: "activityType" as keyof ActivityFeedUnion,
      opsString: "==",
      equality: activityType,
    };

    return firestore.subscribeCollectionWQueries<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      callback,
      [searchOptions],
      undefined,
      250,
      "createdAt",
      "desc"
    );
  },

  subscribeByCardId: (
    shopId: string,
    cardId: string,
    callback: (doc: ActivityFeedUnion[] | undefined) => void
  ) => {
    const searchOptions: SearchOptions<ActivityFeedUnion> = {
      searchParam: "cardId" as keyof ActivityFeedUnion,
      opsString: "==",
      equality: cardId,
    };

    return firestore.subscribeCollectionWQueries<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      callback,
      [searchOptions],
      undefined,
      250,
      "createdAt",
      "desc"
    );
  },

  subscribeByCardNumber: (
    shopId: string,
    cardNumber: string,
    callback: (doc: ActivityFeedUnion[] | undefined) => void
  ) => {
    const searchOptions: SearchOptions<ActivityFeedUnion> = {
      searchParam: "cardNumber" as keyof ActivityFeedUnion,
      opsString: "==",
      equality: cardNumber,
    };

    return firestore.subscribeCollectionWQueries<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      callback,
      [searchOptions],
      undefined,
      250,
      "createdAt",
      "desc"
    );
  },

  subscribeByUser: (
    shopId: string,
    userId: string,
    callback: (doc: ActivityFeedUnion[] | undefined) => void
  ) => {
    const searchOptions: SearchOptions<ActivityFeedUnion> = {
      searchParam: "modifiedBy" as keyof ActivityFeedUnion,
      opsString: "==",
      equality: userId,
    };

    return firestore.subscribeCollectionWQueries<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      callback,
      [searchOptions],
      undefined,
      250,
      "createdAt",
      "desc"
    );
  },

  // Paginated methods for search and pagination functionality
  getPaginatedActivities: async (
    shopId: string,
    pageSize: number = 20,
    lastDocumentSnapshot?: DocumentSnapshot,
    activityType?: ActivityType,
    cardNumber?: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    docs: ActivityFeedUnion[];
    lastVisible: DocumentSnapshot<ActivityFeedUnion>;
    hasMore: boolean;
    totalFetched: number;
  }> => {
    const searchOptions: Array<{
      searchParam: string;
      opsString: any;
      equality: unknown;
    }> = [];

    // Add activity type filter
    if (activityType) {
      searchOptions.push({
        searchParam: "activityType",
        opsString: "==",
        equality: activityType,
      });
    }

    // Add card number filter
    if (cardNumber) {
      searchOptions.push({
        searchParam: "cardNumber",
        opsString: "==",
        equality: cardNumber,
      });
    }

    // Add date range filters
    if (startDate) {
      searchOptions.push({
        searchParam: "createdAt",
        opsString: ">=",
        equality: startDate,
      });
    }

    if (endDate) {
      searchOptions.push({
        searchParam: "createdAt",
        opsString: "<=",
        equality: addDays(endDate, 1), // Include the entire end date
      });
    }

    return firestore.paginateDocumentsWithFilters<ActivityFeedUnion>(
      `shops/${shopId}/activityFeed`,
      pageSize,
      lastDocumentSnapshot,
      undefined,
      "createdAt",
      "desc",
      searchOptions.length > 0 ? searchOptions : undefined
    );
  },

  searchActivities: async (
    shopId: string,
    searchOptions: {
      activityType?: ActivityType;
      cardNumber?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
    }
  ): Promise<ActivityFeedUnion[]> => {
    const filters: Array<{
      searchParam: string;
      opsString: any;
      equality: unknown;
    }> = [];

    // Add filters based on search options
    if (searchOptions.activityType) {
      filters.push({
        searchParam: "activityType",
        opsString: "==",
        equality: searchOptions.activityType,
      });
    }

    if (searchOptions.cardNumber) {
      filters.push({
        searchParam: "cardNumber",
        opsString: "==",
        equality: searchOptions.cardNumber,
      });
    }

    if (searchOptions.startDate) {
      filters.push({
        searchParam: "createdAt",
        opsString: ">=",
        equality: searchOptions.startDate,
      });
    }

    if (searchOptions.endDate) {
      filters.push({
        searchParam: "createdAt",
        opsString: "<=",
        equality: searchOptions.endDate,
      });
    }

    const result =
      await firestore.paginateDocumentsWithFilters<ActivityFeedUnion>(
        `shops/${shopId}/activityFeed`,
        searchOptions.limit ?? 100,
        undefined,
        undefined,
        "createdAt",
        "desc",
        filters.length > 0 ? filters : undefined
      );

    return result.docs;
  },
};
